import React, { useState, useEffect, useRef } from 'react';
import { BaseMessageProps } from '@/shared/types/message';
import { Alert } from '@/modules/Alert';
import { messageService } from '../../services/MessageService';
import { AlertColor } from '@/shared/types/alert';

/** Props de un toast individual renderizado dentro de `MessagePositions`. */
export interface MessageItemProps {
  /** Modelo del mensaje emitido por `MessageService` (texto, color, CTA, `isLeaving`, etc.). */
  message: BaseMessageProps;
}

/**
 * Renderiza un toast del servicio como `Alert` (cierre vuelve a llamar a `messageService.dismiss`).
 *
 * @example
 * import { MessageItem } from '@imj_media/ui';
 *
 * <MessageItem message={singleToastProps} />
 */
export const MessageItem = React.memo(({ message }: MessageItemProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Manejar la visibilidad basada en isLeaving
  useEffect(() => {
    if (message.isLeaving) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [message.isLeaving]);

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const messageId = message.id ?? 0;

    if (process.env.NODE_ENV !== 'production' && !message.id) {
      // eslint-disable-next-line no-console
      console.warn('[MessageItem] message.id is undefined. Using fallback value 0.');
    }

    messageService.dismiss(messageId);

    if (message.onClose) {
      message.onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  // Mapear 'brand' a un color válido de Alert
  // 'brand' se mapea a 'info' porque Alert no tiene soporte nativo para 'brand'
  // TODO: Considerar agregar soporte para 'brand' en Alert o usar estilos personalizados
  const alertColor: AlertColor = message.color === 'brand' ? 'info' : (message.color as AlertColor);

  if (
    process.env.NODE_ENV !== 'production' &&
    !['success', 'danger', 'info', 'warning', 'neutral', 'brand'].includes(message.color)
  ) {
    // eslint-disable-next-line no-console
    console.warn(`[MessageItem] Invalid color "${message.color}". Using fallback.`);
  }

  return (
    <Alert
      color={alertColor}
      variant={message.variant || 'outlined'}
      message={message.message}
      title={message.title}
      description={message.description}
      showCloseButton={message.showCloseButton}
      open={true}
      onClose={handleClose}
      onPrimaryClick={message.onPrimaryClick}
      onSecondaryClick={message.onSecondaryClick}
      primaryButtonText={message.primaryButtonText}
      secondaryButtonText={message.secondaryButtonText}
      isLeaving={message.isLeaving}
      onClick={message.onClick}
      animation={message.animation || 'fade'}
      size={message.size || 'sm'}
      duration={message.duration}
    />
  );
});

MessageItem.displayName = 'MessageItem';
